import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import ComicCard from "@/components/feature/ComicCard";
import { comics, categories } from "@/mocks/comics";

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-900 mb-2">
              漫画未找到
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              您要查看的漫画不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const formatViews = (views: number) => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              {
                label: category ? category.name : "分类",
                href: `/category/${comic.category}`,
              },
              { label: comic.title },
            ]}
          />

          <article className="px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
              <div className="w-full md:w-64 shrink-0">
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 bg-accent-100 text-accent-800 text-xs font-medium px-2.5 py-1 rounded-full">
                    <i className="ri-star-fill text-xs w-3 h-3 flex items-center justify-center"></i>
                    {comic.rating} 分
                  </span>
                  <span className="text-sm text-foreground-500">
                    作者：{comic.author}
                  </span>
                  <span className="text-sm text-foreground-500">
                    {comic.chapters} 话
                  </span>
                  <span className="text-sm text-foreground-500">
                    浏览 {formatViews(comic.views)}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  {category && (
                    <Link
                      to={`/category/${comic.category}`}
                      className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                </div>

                <div className="flex items-center gap-3 text-xs text-foreground-400 mb-5">
                  <span>更新于 {comic.updateDate}</span>
                </div>

                <Link
                  to={`/category/${comic.category}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line w-4 h-4 flex items-center justify-center"></i>
                  开始阅读
                </Link>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-lg font-heading text-foreground-900 mb-4 pb-2 border-b border-background-200/70">
                作品简介
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {comic.description}
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-lg font-heading text-foreground-900 mb-4 pb-2 border-b border-background-200/70">
                精彩内容预览
              </h2>
              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-loose"
                dangerouslySetInnerHTML={{ __html: comic.content }}
              />
            </div>

            {relatedComics.length > 0 && (
              <div className="mb-10">
                <h2 className="text-lg font-heading text-foreground-900 mb-4 pb-2 border-b border-background-200/70">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-product-shop="">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${comic.title} - 羞羞色漫`,
            description: comic.description,
            author: {
              "@type": "Person",
              name: comic.author,
            },
            datePublished: "2026-01-01",
            dateModified: comic.updateDate,
            image: comic.cover,
            url: `https://xiuxiuse.com/comic/${comic.id}`,
            publisher: {
              "@type": "Organization",
              name: "羞羞色漫",
            },
          }),
        }}
      />
    </>
  );
}